import { refsStore, timeStore } from './globalState';
import { TimeUtils } from 'pioneer';
import ar from '../languages/ar';


class TimeManager {
	constructor() {
		/**
		 * Array of time rates.
		 * @type {Array<number>}
		 * @private
		 */
		this._rates = [
			-3600,
			-600,
			-300,
			-120,
			-60,
			-30,
			-10,
			-1,
			0,
			1,
			10,
			30,
			60,
			120,
			300,
			600,
			3600
		];

		/**
		 * Default time rate
		 * @type {number}
		 * @privvate
		 */
		// set default rate time to 1 sec/sec instead of 30 sec/sec - Zahrah
		this._defaultRate = 1;

		/**
		 * Current time rate index
		 * @type {number}
		 * @private
		 */
		this._currentIndex = this._rates.indexOf(this._defaultRate);

		/**
		 * Previous time rate. Used if pausing then playing again.
		 * @type {number}
		 * @private
		 */
		// set default rate time to 1 sec/sec instead of 30 sec/sec - Zahrah
		this._previousTimeRate = 1;

		/**
		 * Date limits for whole app
		 * @type {object<Date, Date>}
		 * @private
		 */
		this.dateLimits = {
			min: null,
			max: null
		};

		// Set pioneer reference.
		const { pioneer } = refsStore.stateSnapshot;
		this.pioneer = pioneer;

		// Binds
		this.setRealTime = this.setRealTime.bind(this);
		this.setDefaultTime = this.setDefaultTime.bind(this);
		this.decreaseRate = this.decreaseRate.bind(this);
		this.increaseRate = this.increaseRate.bind(this);
	}

	/**
	 * Play previous time rate or 1 sec/sec if previous time rate is 0.
	 */
	play() {
		const rate = this._previousTimeRate === 0 ? 1 : this._previousTimeRate;
		this.setTimeRate(rate);
	}

	/**
	 * Pause time. Sets this._previousTimeRate var then sets time rate to 0
	 */
	pause() {
		this._previousTimeRate = this.getTimeRate();
		// set default rate time to 1 sec/sec instead of 30 sec/sec - Zahrah
		this.setTimeRate(1);
	}

	/**
	 * Gets current pioneer time and converts to Date
	 * @returns {Date}
	 */
	getDatetime() {
		const unixTime = TimeUtils.etToUnix(this.pioneer.getTime());

		return new Date(unixTime * 1000);
	}

	/**
	 * Converts to Et and sets pioneer time.
	 * @param {Date} datetime
	 */
	setTime(datetime) {
		const etTime = TimeUtils.unixToEt(datetime.valueOf() * 0.001);

		this.pioneer.setTime(etTime);
	}

	/**
	 * Get current time rate.
	 * @return {Number} Current time rate
	 */
	getTimeRate() {
		return this.pioneer.getTimeRate();
	}

	/**
	 * Check if rate is allowed in the this._rates.
	 * If so, set index, pioneer rate and global states.
	 * @param {Number} rate
	 */
	setTimeRate(rate) {
		const index = this._rates.indexOf(rate);

		if (index !== -1) {
			this._currentIndex = index;
			this.pioneer.setTimeRate(rate);

			timeStore.setGlobalState({
				isPlaying: rate !== 0,
				rate,
				formattedRate: this.getFormattedRate(rate)
			});
		}
	}

	/**
	 * Set time to now and rate to 1 sec/sec.
	 */
	setRealTime() {
		this.setTime(new Date());
		this.setTimeRate(1);
	}

	/**
	 * Set time to now and rate to default rate.
	 */
	setDefaultTime() {
		this.setTime(new Date());
		this.setTimeRate(this._defaultRate);
	}

	/**
	 * Get default time rate
	 */
	getDefaultTimeRate() {
		return this._defaultRate;
	}

	/**
	 * Decrease time rate if value is in the preset list.
	 */
	decreaseRate() {
		this.setIndex(this._currentIndex - 1);
	}

	/**
	 * Increase time rate if value is in the preset list.
	 */
	increaseRate() {
		this.setIndex(this._currentIndex + 1);
	}

	/**
	 * Set time rate index in the preset list.
	 * @param {int} rateIndex
	 */
	setIndex(rateIndex) {
		if (rateIndex < 0 || rateIndex > (this._rates.length - 1)) {
			console.warn(`Time Manager:: rateIndex: ${rateIndex} was out of bounds.`);
			return;
		}

		this.setTimeRate(this._rates[rateIndex]);
	}

	/**
	 * Get display info for time rate.
	 * @returns {object} Rate and unit. E.g. `{ rate: 1, unit: ' sec/sec' }`
	 */
	getFormattedRate() {
		let rate = this._rates[this._currentIndex];
		const hrs = rate / 3600;
		const mins = rate / 60;
		let unit = '';
		if (hrs >= 1 || hrs <= -1) {
			rate = hrs;
			unit = ` ${ar.hr_per_sec}`;
		} else if (mins >= 1 || mins <= -1) {
			rate = mins;
			unit = ` ${ar.min_per_sec}`;
		} else {
			rate %= 60;
			unit = ` ${ar.sec_per_sec}`;
		}
		return { rate, unit };
	}

	/**
	 * @override
	 * @param {Date} date
	 */
	setMin(date) {
		this.dateLimits.min = date;
	}

	/**
	 * @override
	 * @param {Date} date
	 */
	setMax(date) {
		this.dateLimits.max = date;
	}
}

export default TimeManager;
